import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerCloseEvent, DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  #pendingAdds = new Set();

  #loadingAnimation = null;

  /** @type {number} */
  #stickyRaf = 0;

  /** @type {ResizeObserver | null} */
  #stickyObserver = null;

  /** Minimum horizontal travel (px) before a right swipe closes the drawer. */
  #swipeThreshold = 50;

  /** @type {number} */
  #touchStartX = 0;

  /** @type {number} */
  #touchStartY = 0;

  /** Whether a single-finger gesture is currently tracked for swipe-to-close. */
  /** @type {boolean} */
  #swipeTracking = false;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.hasAttribute("data-cart-pending")) this.#startPendingAnimation();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener("bosi:cart-rendered", this.#handleCartRendered);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);

    // Swipe-right-to-close: drawer opens from the right, so "back" = drag right
    // (same mental model as iOS edge-back). Passive listeners keep vertical scroll
    // in .cart-drawer__scroll smooth; decide on touchend.
    const panel = this.#dialog;
    if (panel) {
      panel.addEventListener('touchstart', this.#onTouchStart, { passive: true });
      panel.addEventListener('touchmove', this.#onTouchMove, { passive: true });
      panel.addEventListener('touchend', this.#onTouchEnd, { passive: true });
      panel.addEventListener('touchcancel', this.#onTouchCancel, { passive: true });
    }

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener("bosi:cart-rendered", this.#handleCartRendered);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    this.#teardownStickyObservation();

    const panel = this.#dialog;
    if (panel) {
      panel.removeEventListener('touchstart', this.#onTouchStart);
      panel.removeEventListener('touchmove', this.#onTouchMove);
      panel.removeEventListener('touchend', this.#onTouchEnd);
      panel.removeEventListener('touchcancel', this.#onTouchCancel);
    }
    this.#swipeTracking = false;
    this.#loadingAnimation?.cancel();
  }

  /**
   * @param {TouchEvent} event
   */
  #onTouchStart = (event) => {
    if (!this.#themeDrawer?.isOpen || event.touches.length !== 1) {
      this.#swipeTracking = false;
      return;
    }

    const target = event.target;
    // Don't steal swipes from form fields / range controls.
    if (
      target instanceof Element &&
      target.closest('input, textarea, select, [contenteditable="true"]')
    ) {
      this.#swipeTracking = false;
      return;
    }

    const touch = event.touches[0];
    if (!touch) return;

    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
    this.#swipeTracking = true;
  };

  /**
   * @param {TouchEvent} event
   */
  #onTouchMove = (event) => {
    if (this.#swipeTracking && event.touches.length !== 1) {
      this.#swipeTracking = false;
    }
  };

  /**
   * Close on deliberate right swipe: horizontal past threshold and stronger than
   * vertical (so scrolling the cart list never closes the drawer).
   * @param {TouchEvent} event
   */
  #onTouchEnd = (event) => {
    if (!this.#swipeTracking) return;
    this.#swipeTracking = false;

    if (!this.#themeDrawer?.isOpen) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - this.#touchStartX;
    const deltaY = touch.clientY - this.#touchStartY;

    if (deltaX > this.#swipeThreshold && Math.abs(deltaX) > Math.abs(deltaY)) {
      this.#themeDrawer.close();
    }
  };

  /** @param {TouchEvent} _event */
  #onTouchCancel = (_event) => {
    this.#swipeTracking = false;
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#observeStickyLayout();
    this.#scheduleStickyUpdate();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  #handleDrawerClose = () => {
    this.#teardownStickyObservation();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    if (event.action !== 'add') return;
    const drawer = this.#themeDrawer;
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    if (sourceModal && sourceModal !== this.#dialog) {
      event.promise?.then(({ detail }) => {
        if (!this.hasAttribute('auto-open') || detail?.didError) return;
        const open = () => drawer?.open();
        if (sourceModal.open) sourceModal.addEventListener('close', open, { once: true });
        else open();
      }).catch(() => {});
      return;
    }
    if (!this.hasAttribute('auto-open') && !drawer?.isOpen) return;
    if (!this.#pendingAdds.size) {
      this.toggleAttribute('data-cart-inline', drawer?.isOpen && !this.#isCartEmpty());
    }
    this.#pendingAdds.add(event);
    this.setAttribute('data-cart-pending', '');
    this.removeAttribute('data-cart-error');
    this.setAttribute('aria-busy', 'true');
    this.#startPendingAnimation();
    if (!drawer?.isOpen) drawer?.open();
    event.promise?.then(({ detail }) => {
      if (detail?.didError) this.#settleAdd(event, true);
    }).catch(() => this.#settleAdd(event, true));
  };

  #handleCartRendered = (event) => {
    this.#settleAdd(event.detail.event, event.detail.didError);
  };

  #settleAdd(event, didError) {
    if (!this.#pendingAdds.delete(event)) return;
    if (didError) this.setAttribute('data-cart-error', '');
    if (this.#pendingAdds.size) return;
    this.removeAttribute('data-cart-pending');
    this.#loadingAnimation?.cancel();
    this.#loadingAnimation = null;
    if (!didError) this.removeAttribute('data-cart-inline');
    this.removeAttribute('aria-busy');
    this.#observeStickyLayout();
    this.#scheduleStickyUpdate();
  }

  #startPendingAnimation() {
    if (this.#loadingAnimation) return;
    this.#loadingAnimation = this.querySelector('.bosi-cart-status__ring')?.animate(
      [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
      { duration: 1100, iterations: Infinity, easing: 'linear' }
    ) ?? null;
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /**
   * Remeasure after layout. Cart morph (esp. empty → filled via view-transition)
   * often finishes after the cart:lines promise microtask + a single rAF.
   */
  #scheduleStickyUpdate() {
    cancelAnimationFrame(this.#stickyRaf);
    this.#stickyRaf = requestAnimationFrame(() => {
      this.#updateStickyState();
      this.#stickyRaf = requestAnimationFrame(() => this.#updateStickyState());
    });
  }

  #observeStickyLayout() {
    const dialog = this.#dialog;
    if (!dialog) return;

    if (!this.#stickyObserver) {
      this.#stickyObserver = new ResizeObserver(() => this.#updateStickyState());
    }

    this.#stickyObserver.disconnect();
    this.#stickyObserver.observe(dialog);

    const content = dialog.querySelector('.cart-drawer__content');
    if (content) this.#stickyObserver.observe(content);

    const summary = dialog.querySelector('.cart-drawer__summary');
    if (summary) this.#stickyObserver.observe(summary);
  }

  #teardownStickyObservation() {
    cancelAnimationFrame(this.#stickyRaf);
    this.#stickyRaf = 0;
    this.#stickyObserver?.disconnect();
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const closeButton = dialog.querySelector('.theme-drawer__close-button');
    if (closeButton) {
      const buttonBounds = closeButton.getBoundingClientRect();
      if (buttonBounds.height > 0) {
        const center = buttonBounds.top + buttonBounds.height / 2 - dialog.getBoundingClientRect().top;
        dialog.style.setProperty('--bosi-cart-header-center', `${center}px`);
      }
    }

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    /** @param {'true' | 'false'} value */
    const setSticky = (value) => {
      if (dialog.getAttribute('cart-summary-sticky') !== value) {
        dialog.setAttribute('cart-summary-sticky', value);
      }
    };

    if (!content || !summary) {
      // Keep the default sticky=true. Setting false here used to poison the
      // dialog attribute across hydration morph (PRESERVED_ATTRIBUTES), which
      // then disabled .cart-drawer__items overflow after empty → filled ATC.
      setSticky('true');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    if (drawerHeight <= 0) {
      setSticky('true');
      return;
    }

    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    setSticky(ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}


